/**
 * Single source of truth for the End-User License Agreement, Terms
 * of Service, Privacy Notice, and Use Terms covering The New
 * Jerusalem Codex™ ecosystem.
 *
 * Feb 2026 (v3): the EULA was rewritten end-to-end to the polished
 * 33-section form supplied by the licensor in
 * `New_Jerusalem_Codex_EULA_FULL.md`. The new EULA adds explicit
 * trade-secret declarations (§12), the unified-OS framing (§13),
 * an anti-scraping / anti-model-training clause (§16), structural
 * copyright (§17), derivative-works restriction (§18), patent-
 * eligibility preservation (§27), and Florida governing law (§30)
 * — among other reinforcements. The pastoral/medical carve-out
 * still lives in §2 and §4; the trademark / copyright carve-out
 * lives in §10, §11, §17 (split out from the prior single §6).
 * One document, one acceptance flow.
 *
 * Used by:
 *   - DisclaimerGate: consent gate shown on first entry to each
 *     generation engine. Includes the "By clicking Continue, you
 *     acknowledge…" footer line.
 *   - SiteFooter "Terms" link: read-only re-display for users who
 *     want to re-read the EULA after their session acceptance.
 */
export default function DisclaimerBody({ showConsentFooter = true }) {
  return (
    <div className="rm-disclaimer-body" data-testid="disclaimer-body">
      <p>
        <strong>THE NEW JERUSALEM CODEX™</strong>
        <br />
        <em>
          End-User License Agreement, Terms of Service, Privacy
          Notice, and Use Terms
        </em>
      </p>
      <p>
        <strong>Effective Date:</strong> 2026
      </p>

      <h3 className="rm-eula-heading">1. Agreement to These Terms</h3>
      <p>
        By accessing, using, downloading, or interacting with
        restorationmatrix.org, newjerusalemcodex.org, The New
        Jerusalem Codex™, The Restoration Matrix™, Jacob&rsquo;s
        Ladder Framework™, the Spiritual Warfare Engine™, the
        Discernment Engine™, downloadable educational assets,
        generated PDF outputs, software modules, studies, manuals,
        charts, diagrams, or any related materials within The New
        Jerusalem Codex™ ecosystem, you agree to this End-User
        License Agreement, Terms of Service, Privacy Notice, and Use
        Terms.
      </p>
      <p>
        If you do not agree to these Terms, do not access, use,
        download, copy, save, print, export, distribute, or interact
        with the platform, software, tools, downloads, or generated
        outputs.
      </p>

      <h3 className="rm-eula-heading">2. Nature of the Platform</h3>
      <p>
        The New Jerusalem Codex™ and The Restoration Matrix™ provide
        religious, theological, pastoral, biblical, educational, and
        spiritual reflection tools only.
      </p>
      <p>The platform does not provide:</p>
      <ul>
        <li>medical advice</li>
        <li>clinical therapy</li>
        <li>psychiatric diagnosis</li>
        <li>psychological treatment</li>
        <li>legal advice</li>
        <li>emergency services</li>
        <li>professional healthcare treatment</li>
        <li>licensed counseling services</li>
      </ul>
      <p>
        All generated packets, reports, reflections, charts, studies,
        prayers, guidance, discernment language, or outputs are
        intended for spiritual, pastoral, biblical, devotional, and
        educational use only.
      </p>
      <p>
        The platform is not a substitute for qualified medical,
        psychological, psychiatric, legal, pastoral, or emergency
        support when such support is needed.
      </p>

      <h3 className="rm-eula-heading">3. Emergency and Crisis Use</h3>
      <p>The platform is not an emergency service.</p>
      <p>
        If you may harm yourself or someone else, are in immediate
        danger, are experiencing a medical emergency, psychiatric
        emergency, abuse situation, violent threat, or crisis
        requiring urgent help, stop using the platform and contact
        emergency services, a qualified crisis provider, law
        enforcement, a licensed medical provider, or another
        appropriate emergency resource immediately.
      </p>
      <p>
        The platform cannot respond to emergencies, monitor users,
        provide crisis intervention, or guarantee immediate support.
      </p>

      <h3 className="rm-eula-heading">
        4. Pastoral and Medical Disclaimer
      </h3>
      <p>
        The Restoration Matrix™, Jacob&rsquo;s Ladder Framework™,
        Discernment Engine™, Spiritual Warfare Engine™, and The New
        Jerusalem Codex™ provide spiritual, biblical, pastoral, and
        theological reflection tools.
      </p>
      <p>
        They do not provide medical, psychiatric, psychological,
        clinical, therapeutic, legal, or emergency services.
      </p>
      <p>By using this platform, you acknowledge that:</p>
      <p>
        Healing Packets and generated communications are spiritual
        and educational assets, not medical assessments.
      </p>
      <p>
        Diagnostic language within the platform is used in a
        religious, pastoral, theological, and spiritual-discernment
        sense, not as a licensed clinical diagnosis.
      </p>
      <p>
        Any references to trauma, anxiety, depression, addiction,
        ADHD, emotional distress, family patterns, intrusive
        thoughts, spiritual oppression, strongholds, manifestation
        spirits, or related issues are for spiritual reflection and
        educational discernment only.
      </p>
      <p>
        You are responsible for how you interpret, apply, save,
        share, or rely upon any generated content.
      </p>
      <p>
        You should seek appropriate professional help when medical,
        psychological, psychiatric, legal, safety, or emergency
        concerns are present.
      </p>
      <p>
        Nothing in the platform creates a doctor-patient,
        therapist-client, attorney-client, clergy-penitent,
        counselor-client, fiduciary, or professional healthcare
        relationship.
      </p>

      <h3 className="rm-eula-heading">5. AI Output Limitation</h3>
      <p>
        The platform may use artificial intelligence, automation,
        prompt-based generation, software logic, or other technical
        tools to produce spiritual, biblical, educational,
        devotional, or pastoral content.
      </p>
      <p>
        Generated outputs may be incomplete, inaccurate, unsuitable
        for a particular person, spiritually imperfect, misunderstood,
        misapplied, or affected by user input errors.
      </p>
      <p>
        Users are responsible for reviewing all outputs carefully
        before relying on, saving, sharing, distributing, teaching,
        counseling from, or applying them.
      </p>
      <p>
        The platform does not guarantee that any generated output is
        complete, error-free, spiritually exhaustive, medically
        appropriate, clinically suitable, legally sufficient, or
        applicable to every circumstance.
      </p>

      <h3 className="rm-eula-heading">6. Privacy-First Use</h3>
      <p>
        The Restoration Matrix™ is designed for privacy-first,
        session-based use.
      </p>
      <p>No account or login is required for ordinary use.</p>
      <p>No permanent user profile is intended to be created.</p>
      <p>
        User intake information and generated outputs are intended to
        be processed only for the active session.
      </p>
      <p>
        If the user does not save, export, copy, screenshot, print,
        email, or download the output, the output is intended to
        become inaccessible after the session ends.
      </p>
      <p>
        However, users acknowledge that temporary or transient
        technical processing may occur through hosting providers, AI
        systems, browser functionality, security systems, server
        logs, backups, caching, analytics, or other infrastructure
        necessary to operate, secure, improve, or maintain the
        platform.
      </p>
      <p>
        The platform is designed not to intentionally create
        permanent personal user profiles from ordinary session
        intake, but no internet-based system can guarantee that all
        transient technical records, backups, logs, or third-party
        processing traces are immediately or completely erased.
      </p>
      <p>
        Users should avoid entering unnecessary identifying
        information, including full names, addresses, phone numbers,
        medical records, financial information, passwords, private
        account information, or sensitive details that are not
        needed for spiritual reflection.
      </p>

      <h3 className="rm-eula-heading">
        7. User Responsibility for Saved or Shared Materials
      </h3>
      <p>
        Users are responsible for anything they choose to download,
        copy, save, print, screenshot, email, export, upload,
        distribute, publish, or share.
      </p>
      <p>
        Once a user saves, exports, downloads, screenshots, emails,
        prints, or shares a generated output, the user is responsible
        for protecting that material and any personal or sensitive
        information contained in it.
      </p>
      <p>
        The owner/licensor is not responsible for user-controlled
        storage, sharing, disclosure, publication, misuse, or
        distribution of downloaded or exported materials.
      </p>

      <h3 className="rm-eula-heading">
        8. User Content and Processing Permission
      </h3>
      <p>
        Users retain responsibility for the information they enter
        into the platform.
      </p>
      <p>
        By entering text, answers, descriptions, screenshots, notes,
        or other information into the platform, users grant the
        platform a limited permission to process that information for
        the purpose of generating the requested spiritual, biblical,
        pastoral, educational, devotional, or reflection-based
        output.
      </p>
      <p>
        Users should only enter information they have the right to
        use and should not enter unlawful, abusive, exploitative,
        deceptive, confidential third-party, or unnecessary personal
        information.
      </p>
      <p>
        The platform does not claim ownership of user-submitted
        personal content. However, users remain responsible for the
        legality, accuracy, sensitivity, and appropriateness of the
        information they provide.
      </p>

      <h3 className="rm-eula-heading">
        9. License to Use the Platform and Outputs
      </h3>
      <p>
        Subject to these Terms, users are granted a limited,
        personal, non-exclusive, non-transferable, revocable license
        to access and use the platform and generated outputs for:
      </p>
      <ul>
        <li>personal use</li>
        <li>family use</li>
        <li>ministry use</li>
        <li>church use</li>
        <li>small-group study</li>
        <li>pastoral reflection</li>
        <li>biblical education</li>
        <li>devotional support</li>
      </ul>
      <p>
        Users may not, without written permission from the
        owner/licensor:
      </p>
      <ul>
        <li>sell</li>
        <li>publish</li>
        <li>repackage</li>
        <li>redistribute</li>
        <li>resell</li>
        <li>license or sublicense</li>
        <li>commercially exploit</li>
        <li>monetize</li>
        <li>train competing systems from</li>
        <li>create commercial derivative works from</li>
        <li>copy the platform structure</li>
        <li>recreate the internal logic</li>
        <li>extract the prompt matrices</li>
        <li>replicate the diagnostic routing</li>
        <li>build substantially similar competing systems from</li>
      </ul>
      <p>
        any platform content, software structure, prompt matrices,
        diagrams, charts, hierarchy mappings, framework labels,
        routing systems, diagnostic structures, theological sequences,
        educational layouts, branded materials, or generated outputs.
      </p>

      <h3 className="rm-eula-heading">
        10. Ownership of Intellectual Property
      </h3>
      <p>The New Jerusalem Codex™ ecosystem includes original:</p>
      <ul>
        <li>written expression</li>
        <li>selection, coordination, and arrangement</li>
        <li>chapter structures</li>
        <li>book-series organization</li>
        <li>hierarchy mappings</li>
        <li>diagrams and charts</li>
        <li>educational layouts</li>
        <li>software structure</li>
        <li>framework labels</li>
        <li>naming conventions</li>
        <li>theological sequencing</li>
        <li>diagnostic structures</li>
        <li>spiritual-disorder routing patterns</li>
        <li>pastoral output formats</li>
        <li>manuals</li>
        <li>studies</li>
        <li>packet structures</li>
        <li>visual designs</li>
        <li>generated templates</li>
        <li>prompt architecture</li>
        <li>system organization</li>
      </ul>
      <p>
        These are protected intellectual property of the
        owner/licensor to the fullest extent allowed by law.
      </p>
      <p>
        Biblical truth, Scripture, prayer, repentance, faith, love,
        theological ideas, religious doctrines, spiritual principles,
        and general biblical concepts are not claimed as private
        property.
      </p>
      <p>
        The owner/licensor claims rights in the original expression,
        arrangement, selection, coordination, naming, branding,
        sequencing, diagrams, writings, software organization, prompt
        architecture, platform structure, and presentation system
        through which those truths and principles are expressed.
      </p>

      <h3 className="rm-eula-heading">
        11. Trademarks and Claimed Marks
      </h3>
      <p>
        The following are trademarks or claimed trademarks of the
        owner/licensor:
      </p>
      <ul>
        <li>THE NEW JERUSALEM CODEX™</li>
        <li>THE RESTORATION MATRIX™</li>
        <li>JACOB&rsquo;S LADDER FRAMEWORK™</li>
        <li>THE RESTORATION OF ALL THINGS SERIES™</li>
        <li>LAW OF COVENANT EMERGENCE™</li>
        <li>DOUBLE HELIX OF HISTORY™</li>
      </ul>
      <p>
        Additional names, marks, series titles, product names, engine
        names, framework names, manual titles, study titles, and
        branded phrases within The New Jerusalem Codex™ ecosystem may
        also function as trademarks, service marks, or claimed marks
        of the owner/licensor.
      </p>
      <p>
        Use of these marks does not grant ownership, license,
        endorsement, affiliation, or permission to use them
        commercially.
      </p>
      <p>
        Users may not remove, obscure, imitate, misuse, register,
        copy, rebrand, or use confusingly similar names, marks,
        titles, or branding in connection with competing materials,
        software, books, ministries, frameworks, products, or
        services.
      </p>

      <h3 className="rm-eula-heading">
        12. Trade Secret Declaration
      </h3>
      <p>
        The public-facing content of The New Jerusalem Codex™
        ecosystem may include visible educational, pastoral,
        devotional, theological, and spiritual materials.
      </p>
      <p>
        However, the underlying New Jerusalem Codex™ fail-close AI
        operating system, including its prompt matrices, diagnostic
        routing logic, classification sequences, restoration-ordering
        rules, safety gates, generation constraints,
        spiritual-disorder routing architecture, hidden decision
        trees, software workflows, internal system instructions,
        output-control logic, quality-control patterns, theological
        computation structures, refusal protocols, repair protocols,
        compilation logic, PDF-generation rules, book-generation
        rules, app-routing structure, chat-response architecture, and
        related system design are proprietary trade secrets of the
        owner/licensor.
      </p>
      <p>
        These trade secrets derive independent value from not being
        generally known, not being readily ascertainable, and not
        being publicly disclosed in their complete internal form.
      </p>
      <p>
        Users may not attempt to access, extract, infer, reconstruct,
        scrape, benchmark, expose, copy, reproduce, reverse engineer,
        clone, imitate, or replicate the hidden system logic, routing
        architecture, prompt matrices, internal operational methods,
        diagnostic sequences, fail-close logic, compilation logic,
        chat-response architecture, app-routing structure,
        PDF-generation system, book-generation system, or proprietary
        software architecture of the platform.
      </p>

      <h3 className="rm-eula-heading">
        13. Unified Proprietary Operating System
      </h3>
      <p>
        The New Jerusalem Codex™ ecosystem is not merely a collection
        of separate books, PDFs, manuals, studies, charts, diagrams,
        app pages, chat responses, or downloadable materials.
      </p>
      <p>
        The New Jerusalem Codex™ functions as a unified proprietary
        AI-assisted operating system that governs, routes, compiles,
        filters, sequences, formats, and generates interconnected
        spiritual, biblical, pastoral, educational, diagnostic,
        devotional, and restoration-focused outputs.
      </p>
      <p>This operating system may function as:</p>
      <ul>
        <li>a compiler of books</li>
        <li>a compiler of PDFs</li>
        <li>a compiler of manuals</li>
        <li>a compiler of spiritual reflection packets</li>
        <li>a routing engine for app-based user interaction</li>
        <li>a chat-based theological and pastoral response system</li>
        <li>a diagnostic and restoration sequencing system</li>
        <li>a fail-close AI operating framework</li>
        <li>a generation-control system for branded outputs</li>
        <li>
          a structural brain for The Restoration Matrix™ and related
          modules
        </li>
      </ul>
      <p>
        The visible outputs of the platform, including books,
        packets, manuals, studies, charts, diagrams, app responses,
        chat interactions, and downloadable PDFs, are interlinked
        expressions, executions, or compiled products of the
        underlying New Jerusalem Codex™ operating system.
      </p>
      <p>
        Users may interact with permitted public-facing outputs only
        through authorized platform use. Such interaction does not
        grant access to, ownership of, or license to reproduce the
        underlying operating system, hidden routing logic, prompt
        matrices, diagnostic sequencing, fail-close architecture,
        system rules, generation constraints, or internal compilation
        methods.
      </p>
      <p>
        The owner/licensor reserves all rights in the underlying New
        Jerusalem Codex™ operating system, including its hidden
        architecture, routing logic, prompt structures, diagnostic
        pathways, compilation processes, safety gates, output-control
        rules, theological sequencing, and proprietary methods of
        generating, ordering, filtering, and formatting content.
      </p>
      <p>
        Nothing in these Terms grants users the right to extract,
        infer, reconstruct, imitate, clone, reproduce, train from,
        commercialize, or create a substantially similar system based
        on the visible outputs, user interactions, generated packets,
        app behavior, chat responses, books, manuals, diagrams,
        studies, or downloadable materials.
      </p>

      <h3 className="rm-eula-heading">
        14. Protection of Hidden System Logic
      </h3>
      <p>
        Users may interact with public-facing outputs, downloadable
        assets, studies, manuals, charts, diagrams, and generated
        packets only in the manner permitted by these Terms.
      </p>
      <p>
        Users may not use repeated prompts, automated queries,
        adversarial testing, scraping, benchmarking, output
        comparison, data extraction, prompt injection, jailbreaking,
        reverse prompting, model probing, or similar methods to
        discover, infer, expose, reconstruct, or replicate the hidden
        operating logic of the platform.
      </p>
      <p>
        Any attempt to bypass, defeat, disable, manipulate, extract,
        reveal, or reconstruct the platform&rsquo;s internal
        fail-close system, prompt matrices, diagnostic routes,
        generation rules, refusal gates, or system architecture is
        prohibited.
      </p>

      <h3 className="rm-eula-heading">15. Prohibited Uses</h3>
      <p>Users may not:</p>
      <ul>
        <li>
          reverse engineer, decompile, scrape, or extract system
          logic
        </li>
        <li>
          reconstruct prompt matrices, routing systems, diagnostic
          frameworks, or fail-close logic
        </li>
        <li>
          train AI models, software systems, datasets, or competing
          products using platform content
        </li>
        <li>
          create derivative frameworks or systems substantially
          similar in structure, sequence, hierarchy, nomenclature, or
          logic
        </li>
        <li>
          misrepresent outputs as medical, clinical, therapeutic,
          psychiatric, legal, or emergency advice
        </li>
        <li>
          remove or obscure copyright, trademark, authorship, source,
          watermark, or notice language
        </li>
        <li>
          use the platform for unlawful, abusive, deceptive,
          exploitative, harmful, harassing, defamatory, or invasive
          purposes
        </li>
        <li>
          use the platform to impersonate another person or
          organization
        </li>
        <li>upload content that violates another person&rsquo;s rights</li>
        <li>
          attempt to bypass platform limitations, safeguards, access
          controls, or security systems
        </li>
        <li>
          use automated tools to harvest, analyze, scrape, benchmark,
          or reproduce the platform&rsquo;s outputs or behavior
        </li>
        <li>
          publish generated outputs in a way that suggests
          endorsement, partnership, authorship, or official approval
          without written permission
        </li>
      </ul>

      <h3 className="rm-eula-heading">
        16. Anti-Scraping and Anti-Model-Training Clause
      </h3>
      <p>
        Users may not scrape, harvest, extract, copy, collect,
        automate, or use platform data, prompts, outputs, behavior,
        responses, system patterns, public pages, downloadable
        assets, or generated materials for the purpose of:
      </p>
      <ul>
        <li>training AI models</li>
        <li>fine-tuning AI systems</li>
        <li>creating datasets</li>
        <li>benchmarking</li>
        <li>competitive analysis</li>
        <li>reverse engineering</li>
        <li>replicating the platform</li>
        <li>creating substitute systems</li>
        <li>reconstructing system behavior</li>
        <li>building commercial derivatives</li>
        <li>
          developing similar theological, pastoral, diagnostic,
          spiritual, or educational engines
        </li>
      </ul>
      <p>
        This restriction applies to manual extraction, automated
        extraction, repeated prompting, scraping tools, bots,
        crawlers, scripts, browser automation, prompt attacks, and
        any other method designed to collect, infer, or reproduce
        protected content or hidden system logic.
      </p>

      <h3 className="rm-eula-heading">
        17. Structural Copyright Claim
      </h3>
      <p>
        The owner/licensor claims copyright protection in the
        original expression, wording, arrangement, selection,
        coordination, diagrams, charts, layouts, sequencing, manuals,
        studies, book structures, educational presentation, and
        written explanation of:
      </p>
      <ul>
        <li>The Restoration Matrix™</li>
        <li>Jacob&rsquo;s Ladder Framework™</li>
        <li>The Restoration of All Things Series™</li>
        <li>The New Jerusalem Codex™ materials</li>
        <li>spiritual-disorder routing presentations</li>
        <li>diagnostic and restoration manuals</li>
        <li>theological framework documents</li>
        <li>packet structures</li>
        <li>charts, diagrams, and visual systems</li>
        <li>educational and devotional outputs</li>
      </ul>
      <p>
        The owner/licensor does not claim private ownership of
        biblical truth, Scripture, spiritual realities, prayer,
        repentance, love, faith, pastoral care, or theological ideas
        themselves.
      </p>
      <p>
        The copyright claim applies to the original way these
        materials are selected, arranged, named, written, organized,
        sequenced, illustrated, formatted, presented, and integrated
        within The New Jerusalem Codex™ ecosystem.
      </p>

      <h3 className="rm-eula-heading">
        18. Derivative Works Restriction
      </h3>
      <p>
        Users may not create derivative frameworks, diagnostic
        systems, theological matrices, spiritual-disorder systems,
        pastoral engines, educational structures, software systems,
        manuals, diagrams, charts, book series, or branded platforms
        that are substantially similar in:
      </p>
      <ul>
        <li>arrangement</li>
        <li>hierarchy</li>
        <li>nomenclature</li>
        <li>sequencing</li>
        <li>structural logic</li>
        <li>routing architecture</li>
        <li>diagnostic flow</li>
        <li>restoration-ordering pattern</li>
        <li>prompt architecture</li>
        <li>visual design</li>
        <li>engine behavior</li>
        <li>output structure</li>
      </ul>
      <p>without written permission from the owner/licensor.</p>
      <p>
        This restriction does not prevent users from studying
        Scripture, praying, offering pastoral care, teaching biblical
        truth, discussing theology, or independently developing
        lawful works that do not copy, derive from, or substantially
        replicate the protected expression, branding, system
        architecture, trade secrets, or proprietary structure of The
        New Jerusalem Codex™ ecosystem.
      </p>

      <h3 className="rm-eula-heading">
        19. Downloaded and Exported Materials
      </h3>
      <p>
        Downloaded PDFs, packets, charts, diagrams, manuals, studies,
        prayers, devotional resources, and generated outputs remain
        subject to these Terms.
      </p>
      <p>They may be used for:</p>
      <ul>
        <li>personal use</li>
        <li>family use</li>
        <li>ministry use</li>
        <li>church use</li>
        <li>small-group study</li>
        <li>pastoral reflection</li>
        <li>biblical education</li>
        <li>devotional support</li>
      </ul>
      <p>
        They may not be sold, republished, redistributed, commercially
        exploited, modified into competing materials, stripped of
        notices, used to train AI systems, incorporated into competing
        platforms, or represented as independent original works
        without written permission.
      </p>

      <h3 className="rm-eula-heading">
        20. Scripture and Third-Party Materials
      </h3>
      <p>
        Scripture quotations, public-domain materials, licensed Bible
        translations, historical writings, theological quotations,
        and third-party materials remain subject to their own
        copyright, licensing, attribution, and use requirements where
        applicable.
      </p>
      <p>
        The platform does not claim ownership over Scripture,
        biblical truth, or third-party source material.
      </p>
      <p>
        Where public-domain or properly licensed materials are used,
        the owner/licensor claims rights only in the original
        selection, arrangement, commentary, formatting, integration,
        structure, and presentation created within The New Jerusalem
        Codex™ ecosystem.
      </p>

      <h3 className="rm-eula-heading">21. Minors</h3>
      <p>
        The platform is intended for use by adults or by minors only
        with the knowledge and consent of a parent, guardian, pastor,
        ministry leader, counselor, or other responsible adult where
        appropriate.
      </p>
      <p>
        Because the platform may involve spiritual reflection on
        trauma, family patterns, emotional distress, sin, addiction,
        oppression, or other sensitive topics, minors should not use
        the platform alone for serious personal, emotional,
        spiritual, or safety-related issues.
      </p>
      <p>
        Parents, guardians, and responsible adults are responsible
        for supervising any minor&rsquo;s use of the platform.
      </p>

      <h3 className="rm-eula-heading">
        22. Third-Party Services and Technical Processing
      </h3>
      <p>The platform may rely on third-party services, including:</p>
      <ul>
        <li>hosting providers</li>
        <li>AI systems</li>
        <li>browser functionality</li>
        <li>temporary processing environments</li>
        <li>security tools</li>
        <li>analytics tools</li>
        <li>content delivery systems</li>
        <li>payment or donation processors</li>
        <li>email or download tools</li>
        <li>storage, backup, or logging systems</li>
      </ul>
      <p>
        The owner/licensor is not responsible for outages, failures,
        data handling, policies, actions, or omissions of third-party
        services beyond the owner/licensor&rsquo;s reasonable control.
      </p>
      <p>
        Use of third-party services may be subject to those
        providers&rsquo; own terms, policies, and technical practices.
      </p>

      <h3 className="rm-eula-heading">23. No Warranty</h3>
      <p>
        The platform and all related content, outputs, downloads,
        studies, manuals, software modules, charts, diagrams, and
        generated materials are provided &ldquo;as is&rdquo; and
        &ldquo;as available.&rdquo;
      </p>
      <p>No warranty or guarantee is made regarding:</p>
      <ul>
        <li>uninterrupted service</li>
        <li>error-free operation</li>
        <li>accuracy</li>
        <li>completeness</li>
        <li>spiritual completeness</li>
        <li>medical suitability</li>
        <li>clinical suitability</li>
        <li>legal suitability</li>
        <li>fitness for a particular purpose</li>
        <li>availability</li>
        <li>security</li>
        <li>continued operation</li>
        <li>specific results</li>
        <li>personal transformation</li>
        <li>healing outcome</li>
        <li>ministry outcome</li>
        <li>diagnostic accuracy</li>
        <li>compatibility with every user&rsquo;s situation</li>
      </ul>
      <p>
        Users access and use the platform at their own discretion and
        responsibility.
      </p>

      <h3 className="rm-eula-heading">24. Limitation of Liability</h3>
      <p>
        To the fullest extent permitted by law, the owner/licensor is
        not liable for any direct, indirect, incidental, consequential,
        special, exemplary, punitive, spiritual, emotional, financial,
        reputational, relational, ministry-related, data-related, or
        other damages arising from or related to:
      </p>
      <ul>
        <li>access to the platform</li>
        <li>use of the platform</li>
        <li>inability to use the platform</li>
        <li>interpretation of outputs</li>
        <li>reliance on outputs</li>
        <li>downloaded materials</li>
        <li>shared materials</li>
        <li>user-entered information</li>
        <li>third-party processing</li>
        <li>technical failures</li>
        <li>AI-generated errors</li>
        <li>spiritual counsel generated by the platform</li>
        <li>pastoral reflection materials</li>
        <li>manuals, studies, charts, diagrams, or packets</li>
        <li>unauthorized use by others</li>
        <li>user decisions made after using the platform</li>
      </ul>
      <p>
        This limitation applies whether the claim is based on
        contract, tort, negligence, strict liability, statute, equity,
        or any other legal theory, to the fullest extent permitted by
        law.
      </p>

      <h3 className="rm-eula-heading">25. Indemnification</h3>
      <p>
        Users agree to defend, indemnify, and hold harmless the
        owner/licensor from claims, damages, losses, liabilities,
        costs, and expenses arising from:
      </p>
      <ul>
        <li>user misuse of the platform</li>
        <li>user violation of these Terms</li>
        <li>user-submitted content</li>
        <li>user sharing or publishing of outputs</li>
        <li>user violation of another person&rsquo;s rights</li>
        <li>
          user use of outputs in counseling, ministry, teaching,
          publication, business, or public distribution
        </li>
        <li>
          user misrepresentation of the platform or generated
          materials
        </li>
        <li>user removal of notices, marks, or ownership language</li>
        <li>
          user attempts to copy, extract, reproduce, or compete with
          protected platform materials
        </li>
      </ul>

      <h3 className="rm-eula-heading">26. Reservation of Rights</h3>
      <p>
        All rights not expressly granted are reserved by the
        owner/licensor.
      </p>
      <p>
        No use of the platform grants ownership of any software,
        prompt logic, framework, trade secret, trademark, copyright,
        brand identity, manual, chart, diagram, naming convention,
        system architecture, or proprietary structure.
      </p>
      <p>
        Nothing in these Terms waives the owner/licensor&rsquo;s
        right to pursue copyright registration, trademark
        registration, patent protection, trade secret protection,
        contract enforcement, injunctive relief, damages, or any
        other lawful remedy.
      </p>

      <h3 className="rm-eula-heading">
        27. Patent-Eligibility Preservation
      </h3>
      <p>
        Nothing in this Agreement waives, limits, abandons, or
        disclaims the owner/licensor&rsquo;s right to pursue patent
        protection for any technical, algorithmic, computational,
        software-based, process-based, or system-level innovations
        contained within the platform.
      </p>
      <p>
        Public use of the platform does not grant users any license to
        copy, reproduce, commercialize, or patent the
        owner/licensor&rsquo;s technical, algorithmic, computational,
        or system innovations.
      </p>

      <h3 className="rm-eula-heading">28. Changes to These Terms</h3>
      <p>These Terms may be updated periodically.</p>
      <p>
        Updated Terms may be posted on the platform, included in
        downloadable materials, or otherwise made available.
      </p>
      <p>
        Continued access, use, downloading, or interaction with the
        platform after updates are posted constitutes acceptance of
        the updated Terms.
      </p>

      <h3 className="rm-eula-heading">29. Termination of Access</h3>
      <p>
        The owner/licensor may suspend, restrict, or terminate access
        to the platform or related materials for users who violate
        these Terms, misuse the platform, attempt to extract protected
        system logic, infringe intellectual property, abuse the
        service, or engage in unlawful, harmful, deceptive,
        exploitative, or prohibited conduct.
      </p>

      <h3 className="rm-eula-heading">30. Governing Law</h3>
      <p>
        These Terms are governed by the laws of the State of Florida,
        without regard to conflict-of-law principles, unless another
        jurisdiction is required by applicable law.
      </p>
      <p>
        To the fullest extent permitted by law, any dispute arising
        from or related to these Terms, the platform, generated
        outputs, downloaded materials, intellectual property, trade
        secrets, or user conduct shall be handled in the appropriate
        state or federal courts located in Florida.
      </p>

      <h3 className="rm-eula-heading">31. Severability</h3>
      <p>
        If any provision of these Terms is found invalid, unlawful,
        or unenforceable, the remaining provisions remain in full
        force and effect.
      </p>
      <p>
        Any invalid or unenforceable provision shall be interpreted,
        modified, or limited to the minimum extent necessary to make
        it enforceable while preserving its original intent as much
        as legally possible.
      </p>

      <h3 className="rm-eula-heading">32. Footer Notice</h3>
      <p>
        All app pages, website pages, generated PDFs, studies,
        manuals, charts, diagrams, packet outputs, and downloadable
        materials may include the following notice:
      </p>
      <p>
        <em>
          © 2026 The New Jerusalem Codex Press.
          <br />
          THE NEW JERUSALEM CODEX™ and THE RESTORATION MATRIX™ are
          claimed trademarks of the owner/licensor.
          <br />
          All rights reserved.
        </em>
      </p>
      <p>Where space is limited, the shorter notice may be used:</p>
      <p>
        <em>
          © 2026 The New Jerusalem Codex Press. All rights reserved.
        </em>
      </p>

      <h3 className="rm-eula-heading">33. Complete Agreement</h3>
      <p>
        These Terms constitute the complete agreement between the
        user and the owner/licensor regarding access to and use of
        the platform, software, downloadable assets, generated
        outputs, studies, manuals, charts, diagrams, branded
        materials, and related content.
      </p>
      <p>
        <strong>END OF TERMS</strong>
      </p>

      {showConsentFooter && (
        <p className="rm-disclaimer-consent-line">
          By clicking Continue, you acknowledge and agree to the
          terms above.
        </p>
      )}
    </div>
  );
}
